/**
 * Edge-refinement primitives for the Refine Edge workspace
 * (Tier 1 Group D). Each helper is pure and takes/returns
 * `Uint8Array(w*h)` for binary masks and `HTMLCanvasElement` for
 * soft alpha masks.
 *
 * The workspace composes these in sequence: Smart Radius narrows
 * the band where blurring applies → Smooth + Feather smooth/blur
 * the alpha → Contrast sharpens the edge → Shift Edge dilates or
 * erodes. The output is a soft alpha mask (canvas) that the
 * workspace converts to either a selection or a layer mask.
 */
import { dilateMask, erodeMask, binaryMaskToCanvas } from './mask-morphology';

/** Apply a Gaussian blur to a binary mask, returning a soft alpha
 *  canvas. The mask is first rendered as solid white pixels on a
 *  fresh canvas, then the canvas2d `filter` blur is applied. GPU-
 *  accelerated; same approach as `dilateMask`'s internals.
 *
 *  Used by Feather and by the Smart Radius band-blur path. */
export function featherMaskToCanvas(
  mask: Uint8Array,
  width: number,
  height: number,
  radiusPx: number,
): HTMLCanvasElement {
  const source = binaryMaskToCanvas(mask, width, height);
  if (radiusPx <= 0) return source;
  const out = document.createElement('canvas');
  out.width = width;
  out.height = height;
  const ctx = out.getContext('2d', { willReadFrequently: true });
  if (!ctx) return source;
  ctx.filter = `blur(${radiusPx}px)`;
  ctx.drawImage(source, 0, 0);
  ctx.filter = 'none';
  return out;
}

/** Smooth a binary mask by repeated 3×3 majority vote — every
 *  pixel becomes 1 if ≥5 of its 3×3 neighborhood are 1, else 0.
 *  Photoshop's "Smooth" slider. Output is binary.
 *
 *  `strength` is the slider value 0..100; we map to 0..6 passes.
 *  Each pass costs O(W·H·9); on 4K masks a single pass is ~30ms,
 *  so this caps out at ~180ms at strength=100. */
export function smoothMask(
  mask: Uint8Array,
  width: number,
  height: number,
  strength: number,
): Uint8Array {
  const passes = Math.max(0, Math.min(6, Math.round(strength / 16)));
  if (passes === 0) return new Uint8Array(mask);
  // Two buffers, swapped by reference between passes. The inner
  // loop fully overwrites `dst`, so we never read stale data —
  // pre-existing implementation allocated a fresh Uint8Array per
  // pass (and a useless `dst.set(swap)` copy) which is up to ~50MB
  // of pointless allocation on a 4K mask at strength=100.
  let src = new Uint8Array(mask);
  let dst = new Uint8Array(mask.length);
  for (let p = 0; p < passes; p++) {
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let count = 0;
        for (let dy = -1; dy <= 1; dy++) {
          const yy = y + dy;
          if (yy < 0 || yy >= height) continue;
          for (let dx = -1; dx <= 1; dx++) {
            const xx = x + dx;
            if (xx < 0 || xx >= width) continue;
            if (src[yy * width + xx]) count++;
          }
        }
        dst[y * width + x] = count >= 5 ? 1 : 0;
      }
    }
    [src, dst] = [dst, src];
  }
  return src;
}

/** Sharpen the alpha edge of a soft mask canvas. Maps alpha
 *  values through an S-curve centered at 128, steepness controlled
 *  by `amount` (0..100). 0 = identity, 100 = aggressive (alpha → 0
 *  or 255 with only a 16-step middle band). */
export function contrastMaskCanvas(source: HTMLCanvasElement, amount: number): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = source.width;
  out.height = source.height;
  const ctx = out.getContext('2d', { willReadFrequently: true });
  if (!ctx) return source;
  ctx.drawImage(source, 0, 0);
  if (amount <= 0) return out;
  const id = ctx.getImageData(0, 0, out.width, out.height);
  const d = id.data;
  // S-curve via signed power: gain 1..16 mapped from amount 0..100.
  const gain = 1 + (amount / 100) * 15;
  for (let i = 0; i < d.length; i += 4) {
    const a = d[i + 3];
    if (a === 0 || a === 255) continue;
    // Center, signed scale, recenter, clamp.
    const centered = (a - 128) / 128;
    const sign = centered < 0 ? -1 : 1;
    const mag = Math.pow(Math.abs(centered), 1 / gain);
    let result = 128 + sign * mag * 128;
    if (result < 0) result = 0;
    else if (result > 255) result = 255;
    d[i + 3] = Math.round(result);
    // Keep RGB white so the mask renders correctly when alpha-
    // blended for preview.
    d[i] = 255;
    d[i + 1] = 255;
    d[i + 2] = 255;
  }
  ctx.putImageData(id, 0, 0);
  return out;
}

/** Shift the mask edge inward (negative) or outward (positive) by
 *  N pixels. Wraps `dilateMask` / `erodeMask`; lives here because
 *  it's the slider in the Refine Edge UI, and keeping the
 *  parameter sign-convention in this module aligns with the
 *  workspace's mental model. */
export function shiftEdge(
  mask: Uint8Array,
  width: number,
  height: number,
  pixels: number,
): Uint8Array {
  if (pixels === 0) return new Uint8Array(mask);
  if (pixels > 0) return dilateMask(mask, width, height, pixels);
  return erodeMask(mask, width, height, -pixels);
}

/** Smart Radius: only feather pixels within `radius` of the
 *  binary edge. Implementation: build the "edge band" via
 *  `dilate(mask, r) - erode(mask, r)`. Outside the band, the
 *  output equals the original (full white inside, full transparent
 *  outside); inside the band, the output is the feathered alpha.
 *
 *  This is the Photoshop "Smart Radius" semantic — the user can
 *  request a big blur for shaggy hair edges without losing the
 *  crisp silhouette elsewhere. */
export function smartRadiusFeather(
  mask: Uint8Array,
  width: number,
  height: number,
  radius: number,
): HTMLCanvasElement {
  if (radius <= 0) return featherMaskToCanvas(mask, width, height, 0);

  // Edge band = dilated XOR eroded (the annulus around the
  // boundary). We could compute via Uint8Array math but doing it on
  // canvases lets us reuse the GPU blur for the final composite.
  const dilated = dilateMask(mask, width, height, radius);
  const eroded = erodeMask(mask, width, height, radius);

  const feathered = featherMaskToCanvas(mask, width, height, radius);

  // Compose: copy `feathered` into output, then where outside the
  // band, force full opaque-or-transparent based on `eroded`
  // (interior) / NOT-dilated (exterior).
  const out = document.createElement('canvas');
  out.width = width;
  out.height = height;
  const ctx = out.getContext('2d', { willReadFrequently: true });
  if (!ctx) return feathered;
  ctx.drawImage(feathered, 0, 0);
  const id = ctx.getImageData(0, 0, width, height);
  const d = id.data;
  const len = width * height;
  for (let i = 0; i < len; i++) {
    if (eroded[i]) {
      // Deep interior — force full opacity.
      const di = i * 4;
      d[di] = 255;
      d[di + 1] = 255;
      d[di + 2] = 255;
      d[di + 3] = 255;
    } else if (!dilated[i]) {
      // Far exterior — force transparent.
      const di = i * 4;
      d[di + 3] = 0;
    }
    // Otherwise we're in the edge band: keep the feathered alpha
    // already drawn above.
  }
  ctx.putImageData(id, 0, 0);
  return out;
}
